import { runtimeEnv } from '../../config/runtimeEnv';
import { analyticsIdentityService, type AnalyticsIdentityService } from './AnalyticsIdentity.service';
import type { AnalyticsProvider, AnalyticsUser, AnalyticsUserProperties } from './AnalyticsProvider';

type PosthogRoot = typeof import('posthog-js/dist/module.full.no-external').default;
type PosthogClient = ReturnType<PosthogRoot['init']>;
type WindowWithIdleCallback = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
};

export type AnalyticsCollectionMode = 'standard' | 'pseudonymous';

type PendingCapture = {
  event: string;
  properties: Record<string, unknown>;
};

export class PosthogAnalyticsProvider implements AnalyticsProvider {
  private readonly clients: Partial<Record<AnalyticsCollectionMode, PosthogClient>> = {};
  private readonly initializationStarted = new Set<AnalyticsCollectionMode>();
  private readonly loadGeneration: Record<AnalyticsCollectionMode, number> = {
    standard: 0,
    pseudonymous: 0,
  };
  private readonly pendingCaptures: Record<AnalyticsCollectionMode, PendingCapture[]> = {
    standard: [],
    pseudonymous: [],
  };
  private pendingUser: AnalyticsUser | null = null;
  private pendingUserProperties: AnalyticsUserProperties = {};
  private identifiedUserId: string | null = null;
  private collectionMode: AnalyticsCollectionMode = 'standard';

  constructor(
    private readonly identityService: AnalyticsIdentityService = analyticsIdentityService,
  ) {}

  init(mode: AnalyticsCollectionMode = 'standard'): void {
    this.setCollectionMode(mode);
  }

  setCollectionMode(mode: AnalyticsCollectionMode): void {
    this.collectionMode = mode;
    const inactiveMode = mode === 'standard' ? 'pseudonymous' : 'standard';
    if (inactiveMode === 'standard') {
      this.deactivateClient(inactiveMode);
    }

    if (mode === 'pseudonymous' && this.pendingCaptures.standard.length) {
      this.pendingCaptures.pseudonymous.push(...this.pendingCaptures.standard);
      this.pendingCaptures.standard = [];
      this.cancelScheduledLoad('standard');
    }

    const client = this.clients[mode];
    if (client) {
      this.activateClient(mode, client);
      return;
    }

    this.scheduleClientLoad(mode);
  }

  capture(
    event: string,
    properties: Record<string, unknown>,
    mode: AnalyticsCollectionMode = this.collectionMode,
  ): void {
    const client = this.clients[mode];
    if (client) {
      this.activateClient(mode, client);
      this.captureWithClient(client, mode, event, properties);
      return;
    }

    this.pendingCaptures[mode].push({ event, properties });
    this.scheduleClientLoad(mode);
  }

  disable(): void {
    this.cancelScheduledLoad('standard');
    this.cancelScheduledLoad('pseudonymous');
    this.pendingCaptures.standard = [];
    this.pendingCaptures.pseudonymous = [];
    this.pendingUser = null;
    this.pendingUserProperties = {};
    this.identifiedUserId = null;
    this.deactivateClient('standard');
    this.deactivateClient('pseudonymous');
  }

  identifyUser(user: AnalyticsUser | null): void {
    if (!user || this.collectionMode !== 'standard') {
      return;
    }

    const client = this.clients.standard;
    if (!client) {
      this.pendingUser = user;
      return;
    }

    const distinctId = this.getUserDistinctId(user);
    if (!distinctId) {
      return;
    }

    if (this.identifiedUserId === distinctId && !Object.keys(this.pendingUserProperties).length) {
      return;
    }

    client.identify(distinctId, {
      email: user.email,
      ...this.pendingUserProperties,
    });
    this.identifiedUserId = distinctId;
    this.pendingUserProperties = {};
  }

  setUserProperties(properties: AnalyticsUserProperties): void {
    if (this.collectionMode !== 'standard') {
      return;
    }

    this.pendingUserProperties = {
      ...this.pendingUserProperties,
      ...properties,
    };

    const client = this.clients.standard;
    if (!client || !this.identifiedUserId) {
      return;
    }

    client.identify(this.identifiedUserId, this.pendingUserProperties);
    this.pendingUserProperties = {};
  }

  reset(): void {
    this.pendingUser = null;
    this.pendingUserProperties = {};

    const standardClient = this.clients.standard;
    if (!standardClient) {
      this.identityService.rotateConsentedId();
      return;
    }

    standardClient.reset();
    this.identityService.replaceConsentedId(standardClient.get_distinct_id());
    this.identifiedUserId = null;
  }

  identifiedDistinctId(): string | null {
    return this.identifiedUserId;
  }

  standardDistinctId(): string {
    return this.identityService.consentedId();
  }

  pseudonymousDistinctId(): string {
    return this.identityService.refusedId();
  }

  private scheduleClientLoad(mode: AnalyticsCollectionMode): void {
    if (typeof window === 'undefined' || this.clients[mode] || this.initializationStarted.has(mode)) {
      return;
    }

    const token = this.getProjectToken();
    if (!token) {
      this.pendingCaptures[mode] = [];
      return;
    }

    this.initializationStarted.add(mode);
    this.scheduleLoad(token, mode, ++this.loadGeneration[mode]);
  }

  private async load(
    token: string,
    mode: AnalyticsCollectionMode,
    generation: number,
  ): Promise<void> {
    try {
      const { default: posthog } = await import('posthog-js/dist/module.full.no-external');
      if (generation !== this.loadGeneration[mode] || !this.initializationStarted.has(mode)) {
        return;
      }

      const client = posthog.init(
        token,
        this.clientConfig(mode),
        `palen_${mode}`,
      );
      this.clients[mode] = client;
      this.initializationStarted.delete(mode);

      if (mode === this.collectionMode || mode === 'pseudonymous') {
        this.activateClient(mode, client);
      } else {
        this.deactivateClient(mode);
      }

      this.flushPendingCaptures(mode, client);
      if (mode === 'standard' && this.pendingUser && this.collectionMode === 'standard') {
        this.identifyUser(this.pendingUser);
        this.pendingUser = null;
      }
    } catch {
      if (generation === this.loadGeneration[mode]) {
        this.initializationStarted.delete(mode);
        this.pendingCaptures[mode] = [];
      }
    }
  }

  private clientConfig(mode: AnalyticsCollectionMode) {
    const isPseudonymous = mode === 'pseudonymous';
    return {
      api_host: this.getApiHost(),
      ui_host: 'https://eu.posthog.com',
      bootstrap: {
        distinctID: isPseudonymous
          ? this.identityService.refusedId()
          : this.identityService.consentedId(),
        isIdentifiedID: false,
      },
      defaults: '2026-01-30' as const,
      capture_pageview: false,
      autocapture: false,
      capture_pageleave: !isPseudonymous,
      disable_session_recording: isPseudonymous || this.shouldDisableSessionRecording(),
      disable_surveys: true,
      disable_surveys_automatic_display: true,
      disable_external_dependency_loading: true,
      opt_in_site_apps: false,
      opt_out_persistence_by_default: true,
      persistence: isPseudonymous ? 'memory' as const : 'localStorage+cookie' as const,
      disable_persistence: isPseudonymous,
      person_profiles: isPseudonymous ? 'never' as const : 'identified_only' as const,
      ...(isPseudonymous
        ? {
          persistence_name: 'palen_pseudonymous',
          consent_persistence_name: 'palen_pseudonymous_consent',
        }
        : {}),
    };
  }

  private activateClient(mode: AnalyticsCollectionMode, client: PosthogClient): void {
    if (mode === 'standard' && !client.is_capturing()) {
      client.opt_in_capturing({ captureEventName: false });
    }
    if (mode === 'pseudonymous') {
      client.register({ distinct_id: this.identityService.refusedId() });
      return;
    }
    if (!this.identifiedUserId) {
      client.register({ distinct_id: this.identityService.consentedId() });
    }
  }

  private deactivateClient(mode: AnalyticsCollectionMode): void {
    const client = this.clients[mode];
    if (!client) {
      return;
    }

    if (mode === 'standard') {
      client.opt_out_capturing();
    }
    client.reset(true);
    if (mode === 'standard') {
      this.identifiedUserId = null;
    }
  }

  private captureWithClient(
    client: PosthogClient,
    mode: AnalyticsCollectionMode,
    event: string,
    properties: Record<string, unknown>,
  ): void {
    client.capture(event, {
      ...properties,
      analytics_identity_mode: mode === 'pseudonymous'
        ? 'pseudonymous_refused'
        : this.identifiedUserId
          ? 'authenticated_email'
          : 'anonymous_consented',
      ...(mode === 'pseudonymous' ? { $process_person_profile: false } : {}),
    });
  }

  private flushPendingCaptures(mode: AnalyticsCollectionMode, client: PosthogClient): void {
    const captures = this.pendingCaptures[mode];
    this.pendingCaptures[mode] = [];
    captures.forEach(({ event, properties }) => {
      this.captureWithClient(client, mode, event, properties);
    });
  }

  private cancelScheduledLoad(mode: AnalyticsCollectionMode): void {
    this.initializationStarted.delete(mode);
    this.loadGeneration[mode] += 1;
  }

  private scheduleLoad(
    token: string,
    mode: AnalyticsCollectionMode,
    generation: number,
  ): void {
    this.requestIdleCallback(() => {
      void this.load(token, mode, generation);
    });
  }

  private requestIdleCallback(callback: () => void): void {
    const browserWindow = window as WindowWithIdleCallback;
    if (typeof browserWindow.requestIdleCallback === 'function') {
      browserWindow.requestIdleCallback(callback, { timeout: 2_000 });
      return;
    }
    browserWindow.setTimeout(callback, 500);
  }

  private shouldDisableSessionRecording(): boolean {
    return String(runtimeEnv.VITE_PRODUCTION || '').trim().toLowerCase() !== 'true';
  }

  private getUserDistinctId(user: AnalyticsUser): string | undefined {
    return user.email?.trim().toLowerCase();
  }

  private getProjectToken(): string | undefined {
    return runtimeEnv.VITE_PUBLIC_POSTHOG_KEY || runtimeEnv.VITE_PUBLIC_POSTHOG_TOKEN;
  }

  private getApiHost(): string {
    return runtimeEnv.VITE_POSTHOG_HOST
      || runtimeEnv.VITE_PUBLIC_POSTHOG_HOST
      || 'https://eu.i.posthog.com';
  }
}
